/*
 * Carbisdale - PathPicker
 *
 * Modal-Overlay fuer "Move to folder" im Project Dashboard. Zeigt alle
 * Folder als volle Pfade ("My Projects ▸ Chapters ▸ Drafts"), erlaubt
 * Filter-Eingabe, Pfeil-Navigation, Enter zum Picken und Escape zum
 * Abbrechen. Render-Konventionen analog LinkPicker (drawMenuFrame +
 * Filter-Zeile mit blinkendem Cursor + Trennlinie + drawMenuItem-Liste).
 *
 * Selbst-kontrolliert: das Dashboard liefert die Folder-Liste + Callback,
 * der Picker baut Items und Pfade selbst. Bei Folder-Move filtert der
 * Aufrufer Self+Descendants via `excludeIds` raus, damit kein Zyklus
 * waehlbar ist (Spec §6.3 / §11.8).
 *
 * Pick-API: `onPick(folderId)` wird mit dem Ziel-Folder gerufen (null = Root).
 * Der Dashboard-Code entscheidet anschliessend, ob das ein No-Op ist (wenn
 * folderId === currentFolderId) oder ob er moveProject/moveFolder ruft.
 *
 * Spec: docs/project-dashboard-specification.md §6, §11.8
 */

import { Style } from '../config.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { blurKeyboardSink } from '../touchKeyboard.js'

const MAX_VISIBLE = 8
const MIN_WIDTH = 36
const SIDE_PADDING = 4   // links/rechts Mindest-Luft zum Viewport-Rand
const ITEM_ROW_OFFSET = 3  // Top-Border(1) + Filter(1) + Sep(1)

export class PathPicker {
    constructor() {
        this._active = false
        this._targetType = null      // 'project' | 'folder'
        this._targetId = null
        this._targetName = ''
        this._currentFolderId = null
        this._items = []             // alle Items (unfilterted, sortiert)
        this._filtered = []
        this._filter = ''
        this._index = 0
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        this._bounds = null
        this._onPick = null
        this._onCancel = null
    }

    get isActive() { return this._active }

    /**
     * Oeffnet den Picker.
     *
     * @param {Object} options
     *   @param {'project'|'folder'} options.targetType
     *   @param {string} options.targetId
     *   @param {string} options.targetName - Anzeigename fuers Title-Label
     *   @param {string|null} options.currentFolderId - jetziger Parent (null = Root)
     *   @param {Array<{id, name, parentFolderId}>} options.folders - alle Folder
     *     des Users (Picker baut Pfade darueber auf)
     *   @param {Set<string>|null} [options.excludeIds] - bei Folder-Move: Self +
     *     alle Descendants (Zyklus-Schutz)
     *   @param {(folderId: string|null) => void} options.onPick - Callback bei Auswahl
     *   @param {() => void} [options.onCancel] - Callback bei Escape / Outside-Click
     */
    open(options) {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        const folders = options.folders || []
        const excludeIds = options.excludeIds instanceof Set
            ? options.excludeIds
            : new Set(options.excludeIds || [])
        this._active = true
        this._targetType = options.targetType
        this._targetId = options.targetId
        this._targetName = options.targetName || ''
        this._currentFolderId = options.currentFolderId || null
        this._onPick = typeof options.onPick === 'function' ? options.onPick : null
        this._onCancel = typeof options.onCancel === 'function' ? options.onCancel : null
        this._filter = ''
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        this._bounds = null
        this._items = this._buildItems(folders, excludeIds)
        this._applyFilter()
        // Default-Selektion auf das aktuelle Folder (= (current)-Eintrag) —
        // konservativ: ein versehentliches Enter ist dann ein No-Op und
        // schliesst nur den Picker, ohne das Item zu verschieben.
        const curIdx = this._filtered.findIndex(it => it.folderId === this._currentFolderId)
        this._index = curIdx >= 0 ? curIdx : 0
        this._ensureIndexVisible()
    }

    close() {
        this._active = false
        this._items = []
        this._filtered = []
        this._bounds = null
        this._onPick = null
        this._onCancel = null
    }

    cancel() {
        const cb = this._onCancel
        this.close()
        if (cb) cb()
    }

    /**
     * @returns {boolean} true wenn das Event konsumiert wurde
     */
    handleKey(e) {
        if (!this._active) return false
        switch (e.key) {
            case 'ArrowDown':
                if (this._filtered.length > 0) {
                    this._index = Math.min(this._index + 1, this._filtered.length - 1)
                    this._ensureIndexVisible()
                }
                return true
            case 'ArrowUp':
                if (this._filtered.length > 0) {
                    this._index = Math.max(this._index - 1, 0)
                    this._ensureIndexVisible()
                }
                return true
            case 'Enter':
                if (this._filtered.length > 0) this._acceptIndex(this._index)
                return true
            case 'Escape':
                this.cancel()
                return true
            case 'Backspace':
                if (this._filter.length > 0) {
                    this._filter = this._filter.slice(0, -1)
                    this._applyFilter()
                    this._ensureIndexVisible()
                }
                return true
            case 'Tab':
                this.cancel()
                return true
            default:
                if (typeof e.key === 'string' && e.key.length === 1
                        && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    this._filter += e.key
                    this._applyFilter()
                    this._ensureIndexVisible()
                    return true
                }
                // Modifier/F-Keys etc. — Picker behaelt den Fokus, aber tut nichts
                return true
        }
    }

    /**
     * @returns {boolean} true wenn der Pointer ueber dem Picker liegt
     *   (auch beim Hover ueber das Title/Filter-Band — der Modal blockiert
     *   den Dashboard-Hover komplett).
     */
    handleMouseMove(charX, charY) {
        if (!this._active) return false
        if (!this._bounds) return true
        const b = this._bounds
        const inside = charX >= b.x && charX < b.x + b.width
            && charY >= b.y && charY < b.y + b.height
        if (!inside) return false
        const itemRow = charY - b.y - b.itemRowOffset
        if (itemRow < 0 || itemRow >= b.maxVisible) return true
        const itemIdx = this._scrollOffset + itemRow
        if (itemIdx >= 0 && itemIdx < this._filtered.length && itemIdx !== this._index) {
            this._index = itemIdx
        }
        return true
    }

    /**
     * @returns {boolean} true wenn der Klick konsumiert wurde. Klick
     *   ausserhalb des Pickers cancelt — wird aber trotzdem konsumiert,
     *   damit der Dashboard-handleClick nicht zusaetzlich auf "Klick
     *   ausserhalb = Dashboard schliessen" reagiert.
     */
    handleClick(charX, charY) {
        if (!this._active) return false
        if (!this._bounds) return true
        const b = this._bounds
        const inside = charX >= b.x && charX < b.x + b.width
            && charY >= b.y && charY < b.y + b.height
        if (!inside) {
            this.cancel()
            return true
        }
        const itemRow = charY - b.y - b.itemRowOffset
        if (itemRow >= 0 && itemRow < b.maxVisible) {
            const itemIdx = this._scrollOffset + itemRow
            if (itemIdx >= 0 && itemIdx < this._filtered.length) {
                this._acceptIndex(itemIdx)
                return true
            }
        }
        return true
    }

    /**
     * @param {number} deltaLines - fraktionale Zeilen-Delta (gedrosselter
     *   Wheel-Akkumulator analog LinkPicker)
     * @returns {boolean} true wenn sich der Scroll-Offset geaendert hat
     */
    handleWheel(deltaLines) {
        if (!this._active) return false
        this._wheelAccumulator += deltaLines
        const step = Math.trunc(this._wheelAccumulator)
        if (step === 0) return false
        this._wheelAccumulator -= step
        const maxScroll = Math.max(0, this._filtered.length - MAX_VISIBLE)
        const next = Math.max(0, Math.min(this._scrollOffset + step, maxScroll))
        if (next === this._scrollOffset) return false
        this._scrollOffset = next
        return true
    }

    /**
     * Rendert den Picker als zentriertes Modal ueber dem Dashboard.
     * Erwartet einen frischen Overlay-Zustand vom Aufrufer (Dashboard hat
     * sich schon gerendert) — der Picker malt drueber und setzt zum
     * Schluss seine Bounds fuer Hit-Tests.
     *
     * @param {import('../overlay.js').Overlay} overlay
     * @param {Object} ctx
     *   @param {string} ctx.glyphSeparator - Breadcrumb-Pfeil (Prop: '►', Mono: '>')
     *   @param {boolean} ctx.cursorVisible - aktueller globaler Cursor-Blink-Zustand
     *   @param {Object|null} [ctx.renderer] - fuer Prop-Mode-Pixel-Math beim Breiten-Estimate
     */
    render(overlay, ctx) {
        if (!this._active) { this._bounds = null; return }
        const cols = overlay.cols
        const rows = overlay.rows
        const renderer = ctx?.renderer || null
        const isProp = !!(renderer && renderer.isProportional && renderer.isProportional())
        const glyph = ctx?.glyphSeparator || (isProp ? '►' : '>')
        // Prop: nur Leerzeichen davor (Glyph klebt am naechsten Segment, prop-Breite
        // gibt Luft). Mono: Spaces auf beiden Seiten — gleiche Konvention wie im
        // Dashboard-Breadcrumb (`_renderBreadcrumb`).
        const sep = isProp ? ` ${glyph}` : ` ${glyph} `

        const items = this._filtered
        const maxVisible = Math.min(MAX_VISIBLE, Math.max(items.length, 1))

        // Display-Strings vorberechnen (mit (current)-Suffix).
        const renderedItems = items.map(it => {
            let line = it.segments.join(sep)
            if (it.isCurrent) line += '   (current)'
            return line
        })

        const titleText = `Move "${this._targetName || 'item'}" to`
        // Prompt mit festem Trailing-Space, damit der Filter-Cursor nicht
        // direkt am Doppelpunkt klebt.
        const FILTER_PROMPT = 'Filter: '
        const filterText = FILTER_PROMPT + this._filter

        const cellW = (s, bold = false) => this._textCellWidth(s, renderer, bold)
        // Items werden im Hover-Zustand bold gerendert (drawMenuItem +
        // Selection-Stripe). Bold-Glyphen sind breiter als Regular — wir
        // dimensionieren das Modal in der Bold-Variante, sonst rutscht ein
        // gehoverter langer Pfad ueber die rechte Frame-Kante hinaus.
        let maxItemWidth = 0
        for (const s of renderedItems) {
            const w = cellW(s, true)
            if (w > maxItemWidth) maxItemWidth = w
        }
        // Frame-Padding: 1 Border + 1 Inset links, 1 Border rechts = 3.
        // Plus minimaler interner Spacer 1.
        const titleWidth = cellW(titleText) + 6      // " title " + Border-Bracket-Reserve
        const filterWidth = cellW(filterText) + 4
        let width = Math.max(maxItemWidth + 4, titleWidth, filterWidth, MIN_WIDTH)
        width = Math.min(width, Math.max(MIN_WIDTH, cols - SIDE_PADDING * 2))

        const height = maxVisible + ITEM_ROW_OFFSET + 1

        const x = Math.max(0, Math.floor((cols - width) / 2))
        const y = Math.max(0, Math.floor((rows - height) / 2))

        overlay.drawMenuFrame(
            { x, y, width, height },
            {
                title: titleText,
                borderStyle: Style.MENU_BORDER,
                bgStyle: Style.MENU_BACKGROUND,
                titleStyle: Style.MENU_TITLE
            }
        )

        // Filter-Zeile (1. Innenzeile): zwei Spans nebeneinander —
        //   1) Prompt "Filter: " gedimmt (PANE_INFO, wie die Stats-Labels in
        //      Project Statistics)
        //   2) User-Eingabe in MENU_TITLE mit Filter-Cursor am Ende
        // Cursor sitzt auf dem trailing Space des zweiten Spans (Pattern aus
        // drawLinkPicker), synchron zum globalen Pane-Cursor-Blink.
        const inputX = x + 1
        const promptCells = cellW(FILTER_PROMPT)
        overlay.addTextSpan(inputX, y + 1, FILTER_PROMPT, Style.PANE_INFO, width - 2)
        const userText = `${this._filter} `
        overlay.addTextSpan(
            inputX + promptCells, y + 1, userText, Style.MENU_TITLE,
            Math.max(1, width - 2 - promptCells),
            { cursorPos: this._filter.length, cursorVisible: !!ctx?.cursorVisible }
        )

        // Trennlinie (2. Innenzeile)
        for (let c = 1; c < width - 1; c++) {
            overlay.setChar(x + c, y + 2, '─', Style.MENU_BORDER)
        }

        let scrollStart = 0
        if (items.length === 0) {
            overlay.addTextSpan(
                x + 2, y + ITEM_ROW_OFFSET,
                '(no matches)', Style.PANE_INFO, width - 4
            )
        } else {
            scrollStart = Math.max(0, Math.min(this._scrollOffset, items.length - maxVisible))
            const itemWidth = width - 3   // 1 Border + 1 Inset links, 1 Border rechts
            for (let i = 0; i < maxVisible && scrollStart + i < items.length; i++) {
                const idx = scrollStart + i
                const isSelected = idx === this._index
                overlay.drawMenuItem(
                    x + 2, y + ITEM_ROW_OFFSET + i,
                    {
                        key: null,
                        label: renderedItems[idx],
                        hasSubmenu: false,
                        isCurrent: false,
                        isDisabled: false
                    },
                    { selected: isSelected, itemWidth }
                )
            }
        }

        this._bounds = { x, y, width, height, itemRowOffset: ITEM_ROW_OFFSET, maxVisible }
        // Modal liegt ueber den Pane-Borders — beim Schliessen muessen die
        // Borders neu gemalt werden, sonst bleiben Reste an den Border-Linien
        // stehen. Selbe Begruendung wie im drawLinkPicker — Border-Dirty nur
        // bei tatsaechlichem Bounds-Wechsel, sonst pulst der Cursor-Blink den
        // Border-Layer pro Frame neu.
        overlay.markBorderDirtyIfModalBoundsChanged('pathPicker', {
            x, y, width, height
        })
    }

    /**
     * Test-/Debug-Schnittstelle. Snapshot des internen States.
     */
    getState() {
        return {
            active: this._active,
            filter: this._filter,
            index: this._index,
            scrollOffset: this._scrollOffset,
            items: this._items.slice(),
            filtered: this._filtered.slice(),
            targetType: this._targetType,
            targetId: this._targetId,
            targetName: this._targetName,
            currentFolderId: this._currentFolderId
        }
    }

    // ─────────────────────────────────────────────
    // Private
    // ─────────────────────────────────────────────

    _buildItems(folders, excludeIds) {
        const byId = new Map(folders.map(f => [f.id, f]))
        const segmentsFor = (folderId) => {
            const segs = ['My Projects']
            if (!folderId) return segs
            const chain = []
            let cur = folderId
            const guard = new Set()
            while (cur && !guard.has(cur)) {
                guard.add(cur)
                const f = byId.get(cur)
                if (!f) break
                chain.unshift(f.name || 'Untitled')
                cur = f.parentFolderId || null
            }
            return segs.concat(chain)
        }

        const items = []
        // Root immer dabei (auswaehlbar = "Move to My Projects").
        items.push({
            folderId: null,
            segments: ['My Projects'],
            isCurrent: this._currentFolderId === null
        })
        for (const f of folders) {
            if (excludeIds.has(f.id)) continue
            items.push({
                folderId: f.id,
                segments: segmentsFor(f.id),
                isCurrent: f.id === this._currentFolderId
            })
        }
        // Root steht immer oben. Rest alphabetisch nach Pfad (case-insensitive).
        items.sort((a, b) => {
            if (a.folderId === null) return -1
            if (b.folderId === null) return 1
            const aPath = a.segments.join(' / ').toLowerCase()
            const bPath = b.segments.join(' / ').toLowerCase()
            return aPath.localeCompare(bPath)
        })
        return items
    }

    _applyFilter() {
        const f = this._filter.toLowerCase()
        if (!f) {
            this._filtered = this._items.slice()
        } else {
            this._filtered = this._items.filter(it =>
                it.segments.join(' / ').toLowerCase().includes(f)
            )
        }
        if (this._index >= this._filtered.length) {
            this._index = Math.max(0, this._filtered.length - 1)
        }
        const maxScroll = Math.max(0, this._filtered.length - MAX_VISIBLE)
        this._scrollOffset = Math.min(this._scrollOffset, maxScroll)
    }

    _ensureIndexVisible() {
        const maxVisible = Math.min(this._filtered.length, MAX_VISIBLE)
        if (maxVisible === 0) { this._scrollOffset = 0; return }
        if (this._index < this._scrollOffset) {
            this._scrollOffset = this._index
        } else if (this._index >= this._scrollOffset + maxVisible) {
            this._scrollOffset = this._index - maxVisible + 1
        }
    }

    _acceptIndex(idx) {
        const item = this._filtered[idx]
        if (!item) return
        const cb = this._onPick
        const folderId = item.folderId
        this.close()
        if (cb) cb(folderId)
    }

    _textCellWidth(text, renderer, bold = false) {
        if (!text) return 0
        const codepoints = Array.from(text).length
        const isProp = !!(renderer && renderer.isProportional && renderer.isProportional())
        if (!isProp) return codepoints
        const metrics = renderer._activeMetrics?.(4)
        const cache = metrics?.cache
        if (!cache) return codepoints
        const cw = renderer.charWidth || 1
        const scale = metrics.scaleFactor || 1
        // Bold-Glyphen sind im Prop-Mode breiter als Regular — fuer Sizing-
        // Zwecke (Modal-Breite gegen Hover-Bold) muessen wir das mitnehmen.
        const formatRuns = bold
            ? [{ start: 0, end: codepoints, bold: true }]
            : null
        const lay = computeStringLayout(text, cache, cache.baseStep, formatRuns)
        return Math.max(1, Math.ceil((lay.totalWidth * scale) / cw))
    }
}
